import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    const formData = new FormData(form);

    fetch('https://readdy.ai/api/form/d9spnn94ne17ei0sdls0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed: Record<string, unknown> = {};
        try { parsed = JSON.parse(text); } catch { /* ignore */ }
        if (res.ok && (parsed as { code?: string }).code === 'OK') {
          setFormStatus('success');
          setFormMessage('感谢您的留言，我们会尽快回复！');
          form.reset();
        } else {
          const msg = (parsed as { meta?: { message?: string } })?.meta?.message
            || (parsed as { message?: string })?.message
            || '提交失败，请稍后再试。';
          setFormStatus('error');
          setFormMessage(msg);
        }
      })
      .catch(() => {
        setFormStatus('error');
        setFormMessage('网络错误，请检查连接后重试。');
      });
  };

  return (
    <>
      <SchemaOrg type="Organization" data={{
        name: '女优排行榜',
        url: 'https://readdy.ai',
        description: '女优排行榜是2025年最权威的日本女优排名平台，提供演技、票房、人气等多维度评分数据。',
        foundingDate: '2023',
        address: { '@type': 'PostalAddress', addressCountry: 'JP', addressLocality: 'Tokyo' },
        contactPoint: { '@type': 'ContactPoint', contactType: 'customer service', email: 'info@ranking.example.com' },
      }} />
      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - 女优排行榜',
        url: 'https://readdy.ai/about',
        description: '了解女优排行榜的品牌故事、数据权威性和联系方式。',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>

          {/* Hero */}
          <section className="w-full px-4 md:px-6 pt-6 pb-14">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 font-bold mb-4">
                关于女优排行榜
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                我们致力于为用户提供最新、最权威、最全面的日本女优排名数据与行业资讯
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold mb-8 text-center">
                品牌介绍
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-lightbulb-line text-lg"></i>
                  </div>
                  <h3 className="text-base font-heading text-foreground-950 font-bold mb-3">
                    我们的使命
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    女优排行榜成立于2023年，是中文互联网上最专业的日本女优排行资讯平台。我们聚合了演技实力、票房成绩、粉丝热度等多维度数据，通过科学的评分算法，为用户提供客观、公正、全面的女优排名信息。无论你是资深影迷还是初次了解，都能在这里找到有价值的排行参考。
                  </p>
                </div>
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-shield-check-line text-lg"></i>
                  </div>
                  <h3 className="text-base font-heading text-foreground-950 font-bold mb-3">
                    数据权威性
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们的排名数据来源于FANZA、Amazon Japan、SOD等主流销售平台的公开数据，以及Twitter、Instagram等社交媒体的热度指数。所有数据均经过多平台交叉验证，确保排名的真实性和权威性。我们坚持数据驱动的排名理念，不做人为干预。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold mb-8 text-center">
                行业口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    content: '女优排行榜的数据分析非常专业，多维度排名体系让我们能够从不同角度了解行业动态。已经成为我每周必看的行业资讯平台。',
                    author: '资深影评人 · 张先生',
                  },
                  {
                    content: '作为行业研究者，女优排行榜提供的票房数据和热度指数非常有参考价值，数据的时效性和准确性都令人满意。',
                    author: '娱乐产业分析师 · 李女士',
                  },
                  {
                    content: '新人推荐榜的质量很高，很多我们后来签下的潜力新星都是先从这个榜单上发现的。对于行业从业者来说是非常实用的工具。',
                    author: '经纪公司总监 · 王先生',
                  },
                ].map((review, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 rounded-lg border border-background-200/70 p-5 flex flex-col"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                          <i className="ri-star-fill text-sm"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1">
                      "{review.content}"
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">{review.author}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold mb-3 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-10">
                有任何问题或建议？请通过以下表单与我们联系，我们会尽快回复。
              </p>

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-background-200/70 p-6 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  />
                </div>
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                </button>

                {formStatus === 'success' && (
                  <div className="flex items-center gap-2 p-3 rounded-md bg-green-50 text-green-700 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line"></i>
                    </div>
                    {formMessage}
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 text-red-600 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-close-line"></i>
                    </div>
                    {formMessage}
                  </div>
                )}
              </form>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold mb-10 text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  { q: '女优排行榜是否免费使用？', a: '是的，女优排行榜的所有排名数据和文章内容完全免费向公众开放，无需注册或付费即可浏览。' },
                  { q: '如果想投放广告或进行商业合作，该如何联系？', a: '请通过本页面的联系表单提交合作意向，我们的商务团队会在3个工作日内与您取得联系。' },
                  { q: '排行榜数据可以转载吗？', a: '欢迎转载，但请注明数据来源为"女优排行榜"并附上原文链接。商业用途转载请先与我们联系获取授权。' },
                ].map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/50 transition-colors">
                      <span className="text-sm font-medium text-foreground-950 pr-4">{faq.q}</span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <SchemaOrg type="FAQPage" data={{
            mainEntity: [
              { '@type': 'Question', name: '女优排行榜是否免费使用？', acceptedAnswer: { '@type': 'Answer', text: '是的，女优排行榜的所有排名数据和文章内容完全免费向公众开放，无需注册或付费即可浏览。' } },
              { '@type': 'Question', name: '如果想投放广告或进行商业合作，该如何联系？', acceptedAnswer: { '@type': 'Answer', text: '请通过本页面的联系表单提交合作意向，我们的商务团队会在3个工作日内与您取得联系。' } },
              { '@type': 'Question', name: '排行榜数据可以转载吗？', acceptedAnswer: { '@type': 'Answer', text: '欢迎转载，但请注明数据来源为"女优排行榜"并附上原文链接。商业用途转载请先与我们联系获取授权。' } },
            ],
          }} />
        </main>

        <Footer />
      </div>
    </>
  );
}